import { z } from 'zod';

/**
 * Subscription lifecycle status.
 *
 * - `trialing`  — initial trial period (`trialEndsAt` in future, no charge yet)
 * - `active`    — paid, currentPeriodEnd in future
 * - `past_due`  — payment failed; usable until `pastDueSince + graceDays`
 * - `canceled`  — terminal; no more renewals; access blocked after current period
 * - `paused`    — admin-paused (no renewals, but access kept until admin resumes)
 *
 * On a `usage` subscription only three of these occur. It is `active`
 * from its first settled top-up onwards — including when the bucket is
 * empty, because an empty bucket is a prepaid customer who has not
 * bought again, not a lapsed one. `past_due` and `trialing` cannot
 * happen: nothing is charged in arrears and nothing is granted before
 * payment. `canceled` and `paused` remain available to admins as a way
 * to switch the grant off regardless of the balance.
 *
 * That is why status alone does not answer "may this org act?" for
 * usage — the credits do. See `SubscriptionEntitlementService`.
 */
export const subscriptionStatusSchema = z.enum([
  'trialing',
  'active',
  'past_due',
  'canceled',
  'paused',
]);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;
